import Seo from '../components/Seo.jsx'
import Container from '../components/Container.jsx'

export default function TermsConditions() {
  return (
    <>
      <Seo
        title="Terms & Conditions"
        description="Read the terms and conditions for partnering with Spectranox on your website, app, AI or custom software projects."
        path="/terms"
      />
      <main className="min-h-screen bg-[#020617] pt-32 pb-24 text-slate-100 antialiased">
        <Container className="max-w-4xl">
          <div className="border-b border-slate-800 pb-8">
            <span className="font-mono text-xs uppercase tracking-widest text-blue-500">Legal Agreement</span>
            <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              Terms &amp; Conditions
            </h1>
            <p className="mt-4 text-sm text-slate-400">Last updated: July 2, 2026</p>
          </div>

          <div className="prose prose-invert mt-12 space-y-8 text-slate-300">
            <section className="space-y-3">
              <h2 className="text-xl font-bold text-white">1. Introduction &amp; Acceptance</h2>
              <p className="leading-relaxed">
                Welcome to <strong>Spectranox</strong> ("Company", "we", "our", or "us"). These Terms &amp; Conditions govern your access to and use of our web application development, custom software engineering, mobile applications, AI integration, cyber security consulting, drone development, IoT systems, and other technical services.
              </p>
              <p className="leading-relaxed">
                By signing a project contract, initiating service with us, or using our client portal, you agree to be bound by these terms in full. If you disagree with any part of these terms, you must not use or access our services.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-white">2. Project Scope &amp; Deliverables</h2>
              <p className="leading-relaxed">
                All development projects are executed based on a signed Statement of Work (SOW) or project agreement specifying details, milestones, assets, and targets. Any modifications or additions to the project scope requested during development will be evaluated and may incur additional charges and timelines.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-white">3. Intellectual Property (IP) Ownership</h2>
              <p className="leading-relaxed">
                Unless explicitly agreed otherwise in a customized SOW, all custom-built source code, assets, graphics, and database structures developed specifically for your project will be transferred to you upon receipt of the final, complete project payment.
              </p>
              <p className="leading-relaxed">
                Spectranox retains ownership of pre-existing frameworks, proprietary code libraries, base assets, and general methodologies used during the project's construction.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-white">4. Client Obligations &amp; Materials</h2>
              <p className="leading-relaxed">
                Clients are required to provide all necessary assets (including content copy, high-resolution logos, product images, credentials, and API access) in a timely manner. Spectranox is not liable for project delays caused by a client's failure to provide requested assets.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-white">5. Deployment &amp; Server Hosting</h2>
              <p className="leading-relaxed">
                We support both staging local environments (including XAMPP local server architectures, Apache HTTP, and MySQL/MariaDB engines) and cloud/production web deployments. Unless deployment support is contractually specified, hosting maintenance and domain setup remain the sole responsibility of the client.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-white">6. Warranties &amp; Support Limits</h2>
              <p className="leading-relaxed">
                We offer a standard 30-day warranty post-launch to address bugs, errors, or configuration issues arising from our work. This warranty does not cover modifications made by third parties, issues caused by server hosting environment updates, or design modifications outside the initial SOW.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-white">7. Refund &amp; Cancellation Policy</h2>
              <p className="leading-relaxed">
                This Refund Policy governs payment policies, cancellations, and project refunds for all services provided by Spectranox, including custom website development, mobile apps, software systems, AI automation, cybersecurity auditing, drone engineering, and IoT projects.
              </p>
              <h3 className="text-lg font-semibold text-white mt-4">7.1 Post-Completion Non-Refundability</h3>
              <p className="leading-relaxed">
                Once a project has been fully developed, finalized, and delivered to the client, or deployed live to staging/production servers, <strong>no refunds will be issued under any circumstances</strong>. Full delivery of source code and assets represents the completion of our contractual scope of work.
              </p>
              <h3 className="text-lg font-semibold text-white mt-4">7.2 Mid-Project Cancellation &amp; Pro-Rata Fees</h3>
              <p className="leading-relaxed">
                If a client requests project cancellation in the middle of development, a refund may be calculated and issued on a pro-rata basis under the following guidelines:
              </p>
              <ul className="list-disc list-inside ml-4 space-y-2 mt-2">
                <li>We will audit the developer hours, designs, and database structures completed up to the date of cancellation.</li>
                <li>The cost of third-party assets purchased specifically for the project (such as premium plugins, templates, stock assets, API integrations, hardware prototypes, and drone components) will be charged to the client in full.</li>
                <li>All calculated expenses and the cost of completed development work will be subtracted from any initial deposit, and the remaining balance will be refunded. If the cost of work exceeds the initial deposit, the client will be invoiced for the difference.</li>
              </ul>
              <h3 className="text-lg font-semibold text-white mt-4">7.3 Refund Process</h3>
              <p className="leading-relaxed">
                To request cancellation or inquire about refunds, clients must contact their assigned account manager or submit a formal ticket through the Spectranox Client Portal. Approved refunds will be processed and returned using the original payment method within 10 to 14 business days.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-white">8. Governing Law</h2>
              <p className="leading-relaxed">
                These terms shall be governed by and construed in accordance with the laws applicable to businesses operating within the jurisdiction of our registered offices, without regard to conflict of law principles.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-bold text-white">9. Changes to Terms</h2>
              <p className="leading-relaxed">
                We reserve the right to modify these Terms at any time. Changes will be posted to this page with an updated modification date. Your continued engagement with Spectranox services constitutes acceptance of the new terms.
              </p>
            </section>
          </div>
        </Container>
      </main>
    </>
  )
}
